import { type ReactNode, useEffect, useRef, useState } from 'react';
import { back } from '../lib/router';
import { DESKTOP, useMedia } from '../lib/useMedia';
import { IconButton } from './Button';
import s from './TopBar.module.css';

interface TopBarProps {
  /** Small title shown in the bar once the big title scrolls away. */
  title?: ReactNode;
  backTo?: string | false;
  actions?: ReactNode;
  /** Transparent over a hero until scrolled. */
  overlay?: boolean;
  /** Element whose disappearance reveals the compact title. */
  watch?: React.RefObject<HTMLElement | null>;
  /** Inside a desktop pane, which scrolls itself. */
  pane?: boolean;
}

export function TopBar({ title, backTo = '/', actions, overlay, watch, pane }: TopBarProps) {
  // On the computer the screen sits beside the sidebar, so a bar fixed to the window would
  // cover it; it sticks to the screen's own scroll area instead.
  const desktop = useMedia(DESKTOP);
  const [scrolled, setScrolled] = useState(false);
  const sentinel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const target = watch?.current ?? sentinel.current;
    if (!target) return;
    const io = new IntersectionObserver(([e]) => setScrolled(!e!.isIntersecting), {
      rootMargin: watch && !pane ? '-56px 0px 0px 0px' : '0px',
    });
    io.observe(target);
    return () => io.disconnect();
  }, [watch, pane]);

  return (
    <>
      <div ref={sentinel} className={s.sentinel} aria-hidden="true" />
      <header
        className={[
          s.bar,
          overlay ? s.overlay : '',
          overlay && desktop ? s.inMain : '',
          scrolled ? s.scrolled : '',
          pane ? s.pane : '',
        ].join(' ')}
      >
        <div className={s.side}>
          {backTo !== false && (
            <IconButton icon="arrow-left" label="Back" onClick={() => back(backTo)} />
          )}
        </div>
        <div className={`${s.title} t-subhead`} aria-hidden={!scrolled}>
          {title}
        </div>
        <div className={`${s.side} ${s.end}`}>{actions}</div>
      </header>
    </>
  );
}
